import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { ChangeEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import {
  defaultTherapistTaskAction,
  resetDefaultTherapistAssignAction,
} from 'src/redux/actions/therapistsAction';

type props = {
  setOpenDefaultTherapistDialog: any;
  therapist: any;
};
const AssignDefaultTherapist = (props: props) => {
  const { therapist, setOpenDefaultTherapistDialog } = props;
  const dispatch = useDispatch();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [options, setOptions] = useState<{ id: string; name: string }[]>([]);
  const [popUpMessage, setPopUpMessage] = useState({ type: '', message: '' });
  const defaultTherapistAssignResponse = useSelector(
    (state: any) => state?.defaultThearpistAssignReducer
  );

  useEffect(() => {
    if (therapist?.assignedPatients) {
      const newOptions = Object.keys(therapist?.assignedPatients).map((key: any) => ({
        id: key,
        name: therapist?.assignedPatients[key].name,
      }));
      setOptions(newOptions);
      setSelectedValues(newOptions.map((option) => option.id)); // select all options by default
    }
  }, [therapist]);

  useEffect(() => {
    if (!defaultTherapistAssignResponse?.response) return;
    if (
      defaultTherapistAssignResponse?.response !== null &&
      defaultTherapistAssignResponse?.response?.code !== 'OK'
    ) {
      setPopUpMessage({
        message: defaultTherapistAssignResponse?.response?.message,
        type: 'ERROR',
      });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);
      dispatch(resetDefaultTherapistAssignAction());
      return;
    }
    setPopUpMessage({
      message: 'Default therapist set successfully',
      type: '',
    });
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
      setOpenDefaultTherapistDialog(false);
    }, 3000);
    dispatch(resetDefaultTherapistAssignAction());
  }, [defaultTherapistAssignResponse]);

  const handleValueChange = (event: ChangeEvent<HTMLInputElement>, id: string): void => {
    setSelectedValues((prevSelectedValues: any) =>
      event.target.checked
        ? [...prevSelectedValues, id]
        : prevSelectedValues.filter((value: any) => value !== id)
    );
  };

  const handleSaveBtn = () => {
    dispatch(
      defaultTherapistTaskAction({
        therapistId: therapist?.uuid,
        patientId: selectedValues ? selectedValues : [],
        assign: true,
        disciplineType: therapist?.discipline,
      })
    );
  };

  return (
    <Box>
      <DialogTitle>Set {therapist.name} as default therapist </DialogTitle>
      <DialogContent>
        <Stack gap={'24px'}>
          <Typography
            sx={{
              color: 'var(--text-secondary, #6C737F)',
              fontFamily: 'Inter',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '21.98px',
              marginLeft: '6px',
            }}
          >
            Select patients to assign to this therapist
          </Typography>

          <Box
            sx={{
              maxHeight: '260px',
              overflow: 'scroll',
            }}
          >
            {options.map((option: any) => (
              <Box
                key={option.id}
                sx={{ px: '5px', py: '2px' }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedValues.includes(option.id)}
                      onChange={(event) => handleValueChange(event, option.id)}
                      value={option.id}
                      sx={{
                        padding: '10px',
                      }}
                    />
                  }
                  label={option.name}
                  sx={{
                    width: '100%',
                  }}
                />
              </Box>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={() => setOpenDefaultTherapistDialog(false)}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          autoFocus
          type="submit"
          disabled={!selectedValues?.length}
          onClick={handleSaveBtn}
          sx={{
            whiteSpace: 'nowrap',
            width: 120,
            ':hover': {
              background: '#6366F1',
            },
            '.MuiLoadingButton-loadingIndicator': {
              left: 15,
            },
          }}
          loading={defaultTherapistAssignResponse?.loading}
        >
          Apply
        </LoadingButton>
      </DialogActions>
      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </Box>
  );
};

export default AssignDefaultTherapist;
